import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {BookOfShadowsGameConfig} from "../../BookOfShadowsGameConfig";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {TweenLite} from "gsap";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {BookOfShadowsParsedGameData} from "../../server/data/BookOfShadowsParsedGameData";
import {Button} from "@nolimitcity/slot-game/bin/core/component/Button";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {BitmapCountUp, IBitmapCountUpConfig} from "@nolimitcity/slot-game/bin/core/component/BitmapCountUp";
import {BookOfShadowsTransitionsView} from "../BookOfShadowsTransitionsView";
import {BookOfShadows} from "../../BookOfShadows";
import {BookOfShadowsParsedInitData} from "../../server/data/BookOfShadowsParsedInitData";
import {BookOfShadowsGameMode} from "../../gameMode/BookOfShadowsGameMode";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {IReplayNextPlayerInteraction} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {BookOfShadowsGameModuleConfig} from "../../BookOfShadowsGameModuleConfig";
import {SoundManager} from "../../sound/SoundManager";
import {BookOfShadowsSoundConfig} from "../../BookOfShadowsSoundConfig";
import {BookOfShadowsReelController} from "../../reel/BookOfShadowsReelController";

export interface IBookOfShadowsReplayNextPlayerInteraction extends IReplayNextPlayerInteraction {
    selectedRedraw?: string;
    baseRows?: string;
    lockedReel?: string;
    featureName?: string;
    price?: number
}

export class BookOfShadowsFreeSpinIntroView extends BookOfShadowsTransitionsView {
    private _centerPos: number[] = [BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, (BookOfShadowsGameConfig.instance.REEL_AREA_HEIGHT / 2) + BookOfShadowsGameConfig.instance.REEL_AREA_POS_Y];
    private _scatterSpine: PIXI.spine.Spine;
    private readonly _scatterSymsContainer: PIXI.Container;
    private _fsSpecialSymbol: string = "";
    private _symbolShuffleAnim: TimelineSprite;
    private _shuffleShadowAnim: TimelineSprite;

    private _reDrawButton: Button;
    private _reShuffleText: PIXI.Text;

    private _congrats: PIXI.Text;
    private _youWon: PIXI.Text;
    private _fsAmountLeft: BitmapCountUp;
    private _fsAmountCenter: BitmapCountUp;
    private _fsAmountRight: BitmapCountUp;
    private _freeSpin: PIXI.Text;

    private _fsNumberOfSpins: number[] = [];
    private _fsAmountsPositions: number[] = [];
    private _fsAmountElements: BitmapCountUp[] = [];

    private _arrowItem: PIXI.Sprite;
    private _rowsText: PIXI.Text;

    private _arrowTween: TweenLite;

    private _wasFeatureBuy: boolean = false;
    private _playerChooseRedrawSymbol: boolean = false;

    private _restoreWithReshuffle: boolean = false;

    private _replayInteraction: IBookOfShadowsReplayNextPlayerInteraction;
    private _selectedRowsBase: number;

    /**
     * @constructs
     */
    constructor(layer: ScalableLayer, callBack: () => void) {
        super(layer, callBack);

        this._layer.addChild(this);
        this._scatterSymsContainer = new PIXI.Container();
        this.addChild(this._scatterSymsContainer);
    }

    /**
     * @description To parse the data for the view.
     * @param data
     */
    public onDataParsed(data: BookOfShadowsParsedGameData | BookOfShadowsParsedInitData): void {
        this._fsSpecialSymbol = data.fsSpecialSymbol;
        this._wasFeatureBuy = data.wasFeatureBuy;
        this._playerChooseRedrawSymbol = (data.mode === BookOfShadowsGameMode.FREESPIN_PICK && data.playerChooseRedrawSymbol);
        this._restoreWithReshuffle = false;

        if ((data as BookOfShadowsParsedInitData).isRestoreState) {
            this._restoreWithReshuffle = (data.mode === BookOfShadowsGameMode.FREESPIN_PICK && data.nextMode === BookOfShadowsGameMode.FREESPIN_PICK);
        }

        // added this check so that "this._fsNumberOfSpins" does not get change
        // as it is using active rows for its calculation and this active rows may or may not be changed before choosing redraw
        if (!this._playerChooseRedrawSymbol || (data as BookOfShadowsParsedInitData).isRestoreState) {
            this.calculateFsNumberOfSpins(data.fsNumberOfSpins, data.wasFeatureBuy, data.featureBuyRows);
        }

        this._replayInteraction = (data.replayNextPlayerInteraction as IBookOfShadowsReplayNextPlayerInteraction);
        this._selectedRowsBase = data.selectedRows;
    }

    /**
     * @description To clean up the view.
     */
    public cleanUp(): void {
        this._scatterSpine.visible = false;
        this._symbolShuffleAnim.visible = false;
        this._shuffleShadowAnim.visible = false;
        this._shuffleShadowAnim.stopLoop();
        this._reDrawButton.visible = false;
        this._reShuffleText.visible = false;
        this._congrats.visible = false;
        this._youWon.visible = false;
        this._fsAmountLeft.visible = false;
        this._fsAmountCenter.visible = false;
        this._fsAmountRight.visible = false;
        this._freeSpin.visible = false;
        this._arrowItem.visible = false;
        this._rowsText.visible = false;

        this._button.visible = false;
        this.disableButton();

        if (this._arrowTween) {
            this._arrowTween.pause();
            this._arrowTween.kill();
        }
    }

    /**
     * @description To start the view/s animation by bringing all the scatters to center.
     * @param scatterPositions
     */
    public startAnimation(scatterPositions: number[]): void {
        const duration: number = 1;
        let symPos;

        if (scatterPositions) {
            let sym: StateReelSymbol;

            for (let i: number = 0; i < scatterPositions.length; i++) {
                if (scatterPositions[i] > -1) {
                    sym = Reels.getSymbol(i, scatterPositions[i])!;
                    if (sym) {
                        sym.changeState({
                            state: BookOfShadowsGameConfig.instance.SYMBOL_STATES.normal
                        });

                        symPos = [sym.x + sym.parent.x, sym.y + (sym.parent.parent.y)];
                        this._scatterSymsContainer.addChild(sym);
                        sym.position.set(symPos[0], symPos[1]);

                        TweenLite.to(sym, duration, {
                            x: this._centerPos[0],
                            y: this._centerPos[1]
                        });

                        (Reels.getReel(i)! as BookOfShadowsReelController).fadeAwaySGlowRunes(duration);
                    }
                }
            }

            TweenLite.to(() => {
            }, duration, {
                onComplete: () => {
                    this.playScatterTriggerAnimation(scatterPositions);
                }
            });
        }
    }

    /**
     * @description To react when the row selector button is clicked.
     */
    public rowSelectionClickedInFSIntro(): void {
        this.setNumberOfFreeSpins();
        this.doCarousal();
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        super.initAnimations();

        this._shuffleShadowAnim = this.createShuffleAnim("shuffleShadow", 30);
        this._shuffleShadowAnim.blendMode = PIXI.BLEND_MODES.MULTIPLY;
        this._shuffleShadowAnim.scale.set(2);

        this._symbolShuffleAnim = this.createShuffleAnim("L5/L5Gold", 10);

        this.createRedrawButton();
        this.createTextItems();
        this.createArrowItem();

        // making sure that the button is on top in the view.
        this._button.visible = false;
        this.addChild(this._button);
    }

    /**
     * @description To create all the game graphics for this view.
     */
    protected createTextItems(): void {
        this._reShuffleText = this.createTextItem("One free symbol shuffle", 16, FontWeight.NORMAL);
        this._reShuffleText.position.set(this._reDrawButton.x, this._reDrawButton.y + this._reDrawButton.height / 2 + 70);

        this._rowsText = this.createTextItem("Choose number of Shadow Rows", 16, FontWeight.NORMAL);
        this._rowsText.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2 + 90, (BookOfShadowsGameConfig.instance.GAME_HEIGHT / 2) + 20);

        this._congrats = this.createTextItem("Congratulations!", 22, FontWeight.BOLD);
        this._congrats.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2 + 160, (BookOfShadowsGameConfig.instance.GAME_HEIGHT / 2) - 200);

        this._youWon = this.createTextItem("You won", 22, FontWeight.BOLD);
        this._youWon.position.set(this._congrats.x, (this._congrats.y + this._congrats.height / 2 + 20));

        this.createAmountItems();

        this._freeSpin = this.createTextItem("Free Spins!", 22, FontWeight.BOLD);
        this._freeSpin.position.set(this._congrats.x, (this._fsAmountElements[1].y + this._fsAmountElements[1].height / 2 + 30));
    }

    /**
     * @description To calculate the number of free spins awarded.
     * @param fsNumberOfSpins
     * @param wasFeatureBuy
     * @param featureBuyRows
     */
    private calculateFsNumberOfSpins(fsNumberOfSpins: number[], wasFeatureBuy: boolean, featureBuyRows: number): void {
        let numberOfFs: number[] = [];
        const activeRows: number = BookOfShadowsGameConfig.instance.ACTIVE_ROWS;

        if (wasFeatureBuy) {
            numberOfFs.push(fsNumberOfSpins[featureBuyRows - 3]);
            numberOfFs.push(0);
            numberOfFs.push(0);
        } else {
            numberOfFs.push(fsNumberOfSpins[(activeRows - 3) % 3]);
            numberOfFs.push(fsNumberOfSpins[(activeRows - 3 + 1) % 3]);
            numberOfFs.push(fsNumberOfSpins[(activeRows - 3 + 2) % 3]);
        }
        this._fsNumberOfSpins = numberOfFs;
    }

    /**
     * @description To create the scatter spine.
     */
    private createScatterSpine(): void {
        this._scatterSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("S_spine"));
        this._scatterSpine.position.set(this._centerPos[0], this._centerPos[1]);
        this.addChildAt(this._scatterSpine, 0);
    }

    /**
     * @description To create the symbol shuffle animation.
     * @param assetName
     * @param fps
     */
    private createShuffleAnim(assetName: string, fps: number): TimelineSprite {
        const item: TimelineSprite = new TimelineSprite(GameResources.getTextures(assetName), fps);
        item.visible = false;
        item.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2 - 200, (BookOfShadowsGameConfig.instance.GAME_HEIGHT / 2) - 160);
        item.anchor.set(0.5);
        this.addChild(item);

        return item;
    }

    /**
     * @description To create the reDraw button.
     */
    private createRedrawButton(): void {
        const texture: PIXI.Texture = GameResources.getTextures("reDrawButton")[0];
        this._reDrawButton = new Button({
            idle: texture,
            over: texture,
            down: texture,
            disabled: texture
        }, () => this.reDrawClicked());

        this._reDrawButton.visible = false;
        this._reDrawButton.enabled = false;
        this._reDrawButton.anchor.set(0.5);
        this._reDrawButton.position.set(this._symbolShuffleAnim.x, this._symbolShuffleAnim.y + this._symbolShuffleAnim.height / 2 + 70);
        this.addChild(this._reDrawButton);
    }

    /**
     * @description To create the text item.
     * @param text
     * @param size
     * @param fontWeight
     */
    private createTextItem(text: string, size: number, fontWeight: FontWeight): PIXI.Text {
        const style: TextStyleOptions = {
                fontFamily: "Open Sans",
                fontWeight: fontWeight,
                fill: "#000",
                fontSize: size,
                wordWrap: true,
                wordWrapWidth: 170
            },
            item: PIXI.Text = new PIXI.Text(BookOfShadows.api.translations.translate(text), style);

        item.visible = false;
        item.anchor.set(0.5);
        this.addChild(item);

        return item;
    }

    /**
     * @description To create the amount item.
     */
    private createAmountItem(): BitmapCountUp {
        const style: IBitmapCountUpConfig = {
                style: {
                    font: {
                        size: 20,
                        name: "NumbersWinCountUp"
                    },
                    tint: parseInt(("#ffffff").replace(/^#/, ''), 16)
                },
                numberFormat: {
                    precision: 0
                }
            },
            item: BitmapCountUp = new BitmapCountUp(style);

        item.visible = false;
        this.addChild(item);

        return item;
    }

    /**
     * @description To create the arrow item and its tl.
     */
    private createArrowItem(): void {
        this._arrowItem = new PIXI.Sprite(GameResources.getTextures("arrow")[0]);
        this._arrowItem.visible = false;
        this._arrowItem.anchor.set(0.5);
        this._arrowItem.position.set(this._rowsText.x + this._rowsText.width / 2 + 30, this._rowsText.y);
        this.addChild(this._arrowItem);
    }

    /**
     * @description To perform the carousal effect on teh amount items.
     */
    private doCarousal(): void {
        TweenLite.to(this._fsAmountElements[0], 0.2, {x: this._fsAmountsPositions[1]});
        TweenLite.to(this._fsAmountElements[0].scale, 0.2, {x: 1, y: 1});

        TweenLite.to(this._fsAmountElements[1], 0.2, {x: this._fsAmountsPositions[2]});
        TweenLite.to(this._fsAmountElements[1].scale, 0.2, {
            x: 0.7, y: 0.7, onComplete: () => {
                let item: BitmapCountUp = this._fsAmountElements.pop()!;
                this._fsAmountElements.unshift(item);
            }
        });
        TweenLite.to(this._fsAmountElements[2].scale, 0.14, {
            x: 0, y: 0, onComplete: () => {
                this._fsAmountElements[0].tint = 0xffffff;

                this._fsAmountElements[1].tint = 0x5fa473;
                this._fsAmountElements[2].tint = 0x5fa473;
            }
        });
        TweenLite.to(this._fsAmountElements[2], 0.14, {
            x: this._fsAmountsPositions[1], onComplete: () => {
                this._fsAmountElements[2].x = this._fsAmountsPositions[0] - 50;
                TweenLite.to(this._fsAmountElements[2], 0.06, {x: this._fsAmountsPositions[0]});
                TweenLite.to(this._fsAmountElements[2].scale, 0.06, {x: 0.7, y: 0.7});
            }
        });
    }

    /**
     * @description To create all the amount items.
     */
    private createAmountItems(): void {
        const centerItem: BitmapCountUp = this.createAmountItem();
        centerItem.position.set(this._congrats.x, (this._youWon.y + this._youWon.height / 2 + 40));
        centerItem.tint = 0xffffff;

        const leftItem: BitmapCountUp = this.createAmountItem();
        leftItem.position.set((centerItem.x - centerItem.width / 2 - 80), (this._youWon.y + this._youWon.height / 2 + 40));
        leftItem.tint = 0x5fa473;

        const rightItem: BitmapCountUp = this.createAmountItem();
        rightItem.position.set((centerItem.x + centerItem.width / 2 + 80), (this._youWon.y + this._youWon.height / 2 + 40));
        rightItem.tint = 0x5fa473;

        this._fsAmountElements.push(leftItem);
        this._fsAmountElements.push(centerItem);
        this._fsAmountElements.push(rightItem);

        this._fsAmountsPositions.push(leftItem.x, centerItem.x, rightItem.x);
        this._fsAmountLeft = leftItem;
        this._fsAmountCenter = centerItem;
        this._fsAmountRight = rightItem;
    }

    /**
     * @description To set number of free spins in the amount items.
     */
    private setNumberOfFreeSpins(): void {
        this._fsAmountLeft.getCountUpAnimation(this._fsNumberOfSpins[1], this._fsNumberOfSpins[1], {duration: 0});
        this._fsAmountLeft.pivot.set(this._fsAmountLeft.width / 2, this._fsAmountLeft.height / 2);

        this._fsAmountCenter.getCountUpAnimation(this._fsNumberOfSpins[0], this._fsNumberOfSpins[0], {duration: 0});
        this._fsAmountCenter.pivot.set(this._fsAmountCenter.width / 2, this._fsAmountCenter.height / 2);

        this._fsAmountRight.getCountUpAnimation(this._fsNumberOfSpins[2], this._fsNumberOfSpins[2], {duration: 0});
        this._fsAmountRight.pivot.set(this._fsAmountRight.width / 2, this._fsAmountRight.height / 2);
    }

    /**
     * @description To play the scatter trigger spine animation.
     * @param scatterPositions
     */
    private playScatterTriggerAnimation(scatterPositions: number[]): void {
        let sym: StateReelSymbol;
        for (let i: number = 0; i < scatterPositions.length; i++) {
            if (scatterPositions[i] > -1) {
                sym = Reels.getSymbol(i, scatterPositions[i])!;
                if (sym) {
                    sym.visible = false;
                }
            }
        }

        this._scatterSymsContainer.children.length = 0;
        this.createScatterSpine();

        let tween: SpineTween = new SpineTween(this._scatterSpine, 0, "S_Trigger", 0);
        TweenLite.fromTo(this._scatterSpine, 0.001, {alpha: 0}, {alpha: 1});

        TweenLite.to(() => {
        }, tween.duration() + 0.2, {
            onComplete: () => {
                tween.clear();
                tween.kill();
                this.playShuffleAnimation();
            }
        });
    }

    /**
     * @description To play the symbol shuffle animation with random symbols' textures.
     */
    private playShuffleAnimation(calledFromRedraw: boolean = false): void {
        const len: number = 13,
            syms: string[] = ["M1", "M2", "M3", "M4", "M5", "L1", "L2", "L3", "L4", "L5"];

        let textures: PIXI.Texture[] = [],
            symIndex: number;

        for (let i: number = 0; i < len; i++) {
            symIndex = MathHelper.randomIntInRange(0, syms.length - 1);
            textures.push(GameResources.getTextures(syms[symIndex] + "/" + syms[symIndex] + "Gold")[0]);
        }

        !calledFromRedraw && textures.push(GameResources.getTextures(this._fsSpecialSymbol + "/" + this._fsSpecialSymbol + "Gold")[0]);

        (this._symbolShuffleAnim as any)._textures = textures;
        this._symbolShuffleAnim.visible = true;
        SoundManager.playFSChooseSymbol();
        this._symbolShuffleAnim.getAnimation(undefined, undefined, () => {
            if (calledFromRedraw) {
                if (this._playerChooseRedrawSymbol) {
                    (this._symbolShuffleAnim as any)._textures.push(GameResources.getTextures(this._fsSpecialSymbol + "/" + this._fsSpecialSymbol + "Gold")[0]);
                    this._symbolShuffleAnim.setFrame(this._symbolShuffleAnim.totalFrames - 1);

                    this.enableButton();
                    if (GameSetting.replayMode) {
                        this.handleReplay();
                    }

                    SoundManager.stopSoundEffect(BookOfShadowsSoundConfig.instance.FS_CHOOSE_SYMBOL_LOOP);
                } else {
                    this.playShuffleAnimation(true);
                }
            } else {
                SoundManager.stopSoundEffect(BookOfShadowsSoundConfig.instance.FS_CHOOSE_SYMBOL_LOOP);

                TweenLite.delayedCall(0.1, () => this.symbolShuffleAnimationCompleted());
            }
        });

        this._shuffleShadowAnim.visible = true;
        this._shuffleShadowAnim.playLoop();

        !calledFromRedraw && this.turnOnRightPageItems();
    }

    /**
     * @description To make the remaining items visible on the screen.
     */
    private turnOnRightPageItems(): void {
        this._fsAmountLeft = this._fsAmountElements[0];
        this._fsAmountCenter = this._fsAmountElements[1];
        this._fsAmountRight = this._fsAmountElements[2];

        this.setNumberOfFreeSpins();

        this._fsAmountElements[0].scale.set(0.7);
        this._fsAmountElements[1].scale.set(1);
        this._fsAmountElements[2].scale.set(0.7);

        this._fsAmountCenter.visible = true;

        this._congrats.visible = true;
        this._youWon.visible = true;
        this._freeSpin.visible = true;

        if (this._wasFeatureBuy) {
            return;
        }

        this._fsAmountLeft.visible = true;
        this._fsAmountRight.visible = true;

        this._rowsText.visible = true;
        this._arrowItem.visible = true;

        const pos: number[] = [this._arrowItem.x + this._arrowItem.width / 2 + 15, this._arrowItem.y];

        BookOfShadowsGameModuleConfig.inactiveRowController.symbolShuffleFSCompleted(pos);

        this._arrowItem.x = (this._rowsText.x + this._rowsText.width / 2 + 30);
        this._arrowTween = TweenLite.to(this._arrowItem, 0.3, {
            x: this._arrowItem.x + 5,
            onComplete: () => this.playArrowTl()
        });
    }

    /**
     * @description To play the arrow animation indicating towards row selector.
     */
    private playArrowTl(): void {
        if (this._arrowItem.visible) {
            this._arrowTween = TweenLite.to(this._arrowItem, 0.3, {
                x: this._arrowItem.x - 10, onComplete: () =>
                    TweenLite.to(this._arrowItem, 0.3, {
                        x: this._arrowItem.x + 10,
                        onComplete: () => this.playArrowTl()
                    })
            });
        }
    }

    /**
     * @description When the symbol shuffle animation is completed.
     */
    private symbolShuffleAnimationCompleted(): void {
        if (GameSetting.replayMode) {
            if (!this._replayInteraction.selectedRedraw) {
                this.handleReplay();
            } else {
                TweenLite.delayedCall(0.3, () => this.reDrawClicked());
            }
        }

        if (this._restoreWithReshuffle) {
            this._button.visible = true;
            this.enableButton();

            return;
        }

        this._reShuffleText.visible = true;
        TweenLite.fromTo(this._reShuffleText, 0.2, {alpha: 0}, {alpha: 1});

        this._reDrawButton.visible = true;
        TweenLite.fromTo(this._reDrawButton, 0.2, {alpha: 0}, {
            alpha: 1, onComplete: () => {
                this._reDrawButton.enabled = true;
                this._button.visible = true;
                this.enableButton();
            }
        });
    }

    /**
     * @description To react when the reDraw option is chosen.
     */
    private reDrawClicked(): void {
        this._reDrawButton.visible = false;
        this._reShuffleText.visible = false;

        this._reDrawButton.enabled = false;
        this.disableButton();

        BookOfShadowsGameConfig.instance.REDRAW_ON_FS_CLICKED = true;
        this._completeCallback();

        this.playShuffleAnimation(true);
    }

    /**
     * @description To handle the replay round.
     */
    private handleReplay(): void {
        let index: string;
        if (this._wasFeatureBuy) {
            index = (this._selectedRowsBase - 2) + "";
        } else {
            index = this._replayInteraction.selectedIndex + "";
        }

        if (!this._wasFeatureBuy) {
            let rotateBy: number = Math.abs(((this._selectedRowsBase - 2) - parseInt(this._replayInteraction.selectedIndex + "")));
            if ((this._selectedRowsBase === 5 && this._replayInteraction.selectedIndex + "" === "1")) {
                rotateBy = 1;
            }
            if ((this._selectedRowsBase === 4 && this._replayInteraction.selectedIndex + "" === "1")
                || (this._selectedRowsBase === 5 && this._replayInteraction.selectedIndex + "" === "2")) {
                rotateBy = 2;
            }
            this.leftRotateArray(this._fsNumberOfSpins, rotateBy);

            this._reDrawButton.enabled = false;

            this._fsAmountLeft = this._fsAmountElements[0];
            this._fsAmountCenter = this._fsAmountElements[1];
            this._fsAmountRight = this._fsAmountElements[2];
            this.setNumberOfFreeSpins();
            this._fsAmountElements[1].tint = 0xffffff;
            this._fsAmountElements[1].scale.set(1);

            this._fsAmountElements[0].tint = 0x5fa473;
            this._fsAmountElements[0].scale.set(0.7);
            this._fsAmountElements[2].tint = 0x5fa473;
            this._fsAmountElements[2].scale.set(0.7);
        }

        BookOfShadowsGameModuleConfig.inactiveRowController.handleReplay(index);

        TweenLite.delayedCall(1, () => this._completeCallback());
    }

    /**
     * @description To left rotate any number array.
     * @param arr The array to be rotated.
     * @param rotateBy The array to be rotated by.
     */
    private leftRotateArray(arr: number[], rotateBy: number): void {
        const gcd: number = this.gcd(rotateBy, arr.length);

        for (let i = 0; i < gcd; i++) {
            /* move i-th values of blocks */
            let temp = arr[i];
            let j = i;

            while (true) {
                let k = j + rotateBy;
                if (k >= arr.length)
                    k = k - arr.length;

                if (k == i)
                    break;

                arr[j] = arr[k];
                j = k;
            }
            arr[j] = temp;
        }
    }

    /**
     * @description To calculate gcd of two numbers.
     * @param a
     * @param b
     */
    private gcd(a: number, b: number): number {
        if (b == 0)
            return a;
        else
            return this.gcd(b, a % b);
    }
}
